import { AuthUser } from "@app/auth/model/auth-user";
import { DbHelper } from "@app/shared/model/db-object.model";
import { ImportMap } from "@app/shared/resources/class-importer-exporter.service";
import { ROLE_NAMES } from "@app/user/model/user-role";
import { ProjectViewColumn } from "../constants/all-view-columns.constants";
import { DEFAULT_COORDINATOR_VIEW_COLUMNS, DEFAULT_PM_VIEW_COLUMNS, DEFAULT_VIEW_COLUMNS } from "../constants/default-views.constants";

export interface ProjectView {
    id: number;
    userinfoId: number;
    entityId?: number;
    name: string;
    viewData: string[];
    isDefault: boolean;
}

export interface ProjectViewData {
    id: number;
    userinfo_id: number;
    entity_id?: number;
    name: string;
    view_data: string[];
    is_default: boolean;
}

export class ProjectViewHelper extends DbHelper<ProjectView, ProjectViewData> {
    dbTypeName = "Project View";

    readonly importMap: ImportMap<ProjectView, ProjectViewData> = [
        { i: "id", o: "id" },
        { i: "name", o: "name" },
        { i: "entity_id", o: "entityId" },
        { i: "view_data", o: "viewData" },
        { i: "userinfo_id", o: "userinfoId" },
        { i: "is_default", o: "isDefault" },
    ];

    static getDefaultProjectView(entityId: number, userinfoId: number): ProjectView {
        return ProjectViewHelper.getProjectView(entityId, userinfoId, DEFAULT_VIEW_COLUMNS);
    }

    static getPropertyManagerView(entityId: number, userinfoId: number, isAdmin: boolean): ProjectView {
        return ProjectViewHelper.getProjectView(entityId, userinfoId, isAdmin ? DEFAULT_PM_VIEW_COLUMNS : DEFAULT_COORDINATOR_VIEW_COLUMNS);
    }

    static getDefaultViewForRole(authUser: AuthUser) {
        // if a default projectview doesn't exist for the user,
        // set the view to the clare default project view
        const pmRoles: string[] = [ROLE_NAMES.PROPERTY_MANAGER_COORDINATOR, ROLE_NAMES.PROPERTY_MANAGER_ADMIN];
        if (pmRoles.includes(authUser.role.name)) {
            return ProjectViewHelper.getPropertyManagerView(
                authUser.entityId,
                authUser.id,
                authUser.role.name === ROLE_NAMES.PROPERTY_MANAGER_ADMIN
            );
        }
        return ProjectViewHelper.getDefaultProjectView(authUser.entityId, authUser.id);
    }

    fromDbObject(projectView): ProjectView {
        return {
            id: projectView[this.importMap[0].i],
            name: projectView[this.importMap[1].i],
            entityId: projectView[this.importMap[2].i],
            viewData: projectView[this.importMap[3].i],
            userinfoId: projectView[this.importMap[4].i],
            isDefault: projectView[this.importMap[5].i],
        };
    }

    private static getProjectView(entityId: number, userinfoId: number, unmappedColumns: ProjectViewColumn[]): ProjectView {
        return {
            id: null,
            name: "Default Account View",
            entityId,
            viewData: unmappedColumns.map(col => col.viewKey),
            userinfoId,
            isDefault: false,
        };
    }
}
